import axios from "axios";
import api, { setupAxios } from "./base";
import { identifyUser, resetAnalytics } from "@/analytics";
import { wipeQueryCache } from "@/lib/queryPersister";

const AUTH_URL = "auth/";

// SEC-1: the auth routes answer 429 past their caps.
export const TOO_MANY_ATTEMPTS =
  "Too many attempts. Try again in a few minutes.";
export const isThrottled = (err: unknown) =>
  (err as { response?: { status?: number } })?.response?.status === 429;

export const login = (email: string, password: string) =>
  api
    .post(`${AUTH_URL}token/login/`, {
      email: email.toLowerCase(),
      password,
    })
    .then((response) => {
      if (response.status >= 400) {
        throw response;
      }
      if (response.data.authToken) {
        localStorage.setItem("token", response.data.authToken);
        setupAxios(response.data.authToken);
      }
      return response.data;
    })
    .catch((e) => {
      logout();
      throw e;
    });

export const logout = () => {
  const token = localStorage.getItem("token");
  setupAxios();
  if (token) {
    api
      .post(`${AUTH_URL}token/logout/`, token, {
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .catch(() => {});
    localStorage.removeItem("token");
  }
  // Persisted compliance data MUST NOT survive session boundaries.
  // The next user on this browser sees a cold cache, not the prior
  // session's portfolio.
  wipeQueryCache();
  resetAnalytics();
  return null;
};

export const loadUser = () => {
  if (!localStorage.getItem("token")) {
    return null;
  }
  return api
    .get(`user/me/`)
    .then((resp) => {
      if (resp.data?.id) identifyUser(resp.data);
      return resp.data;
    })
    .catch((err) => {
      // Only a 401 (DRF TokenAuthentication: invalid or inactive) ends the
      // session. Network errors and 5xx (Django autoreload) rethrow so
      // useMeQuery retries (TanStack default, 3) and keeps prior user data.
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        return logout();
      }
      throw err;
    });
};

export const exchangeAuthorizationCode = (data: any) =>
  api
    .post(`utility-authorizations/use_authorization_code/`, data)
    .then((resp) => resp.data);

export const passwordReset = (email: string) =>
  api.post("password_reset/", { email }).then((resp) => resp.data);

export const passwordResetConfirm = (token: string, password: string) =>
  api
    .post("password_reset/confirm/", { token, password })
    .then((resp) => resp.data);

// Invite-claim (Set-your-password). resolve maps a reset-token key to a claim
// state (+ prefill); name persists first/last name scoped to that token. Bodies
// are authored camelCase (base.ts decamelizes the wire); responses arrive
// camelCase (base.ts camelizes them).
export type InviteState = "valid" | "expired" | "used" | "invalid" | "revoked";

export interface InviteResolution {
  state: InviteState;
  email?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  buildingsCount?: number;
  invitedBy?: string | null;
}

export const resolveInvite = (token: string): Promise<InviteResolution> =>
  api.post("account/invite/resolve/", { token }).then((resp) => resp.data);

export const setInviteName = (
  token: string,
  firstName: string,
  lastName: string,
): Promise<{ firstName: string; lastName: string }> =>
  api
    .post("account/invite/name/", { token, firstName, lastName })
    .then((resp) => resp.data);

export const activateAccount = (uid: string, token: string): Promise<void> =>
  api
    .get(`account/activate/${uid}/${token}/`)
    .then((resp) => resp.data)
    .catch((error) => {
      throw error.response?.data?.detail || "Account activation failed";
    });

export const resendActivation = (email: string): Promise<void> =>
  api
    .post("/resend-activation/", { email })
    .then((resp) => resp.data)
    .catch((error) => {
      throw error.response?.data?.detail || "Failed to resend activation email";
    });

export const registerUser = (userData: {
  first_name: string;
  last_name: string;
  company_name: string;
  email: string;
  password?: string;
  bin?: string;
  rewireId?: string;
}) => api.post("/user/register/", userData).then((resp) => resp.data);
